import { Typography } from "components/typography";
import { Getter } from "@tanstack/react-table";
import { format } from "date-fns";
import { TFunction } from "react-i18next";
import { FeedbackLockReason } from "api/types";
import { formatRelativeDate } from "../formatRelativeDate";
import { AssignedCellWithIcon } from "../tablecells/AssignedCellWithIcon";
import { FeedbackDateCellText } from "../tablecells/FeedbackDateCellText";
import { IcbtFeedbackStateCell } from "../tablecells/IcbtFeedbackStateCell";
import { PatientNotesCellText } from "../tablecells/PatientNotesCellText";
import { ProgramTreatmentCellText } from "../tablecells/ProgramTreatmentCellText";
import { StatusInProgressCell } from "../tablecells/StatusCompletedCell";
import {
  AssignmentIcon,
  CheckCircleIcon,
  IconTextContainer,
  LockIcon,
} from "./styles";
import { AssignedTableRow, HighlightStatus, ProgramStatus } from "./types";
import { getFeedbackLockTranslationKey } from "./utils";
import { Tooltip } from "@mindlercare/ui-web";

export const programTitleCell = ({
  t,
  getValue,
  openPreviewModal,
  status: rowStatus,
  isLocked,
  nextScheduledExecution,
}: {
  t: TFunction<"translation", undefined>;
  getValue: Getter<AssignedTableRow["title"]>;
  openPreviewModal: ({
    programId,
    isLocked,
  }: {
    programId: string;
    isLocked?: boolean;
  }) => void;
  status: HighlightStatus;
  isLocked?: boolean;
  nextScheduledExecution?: Date | null;
}) => {
  const { programId, programTitle, treatmentTitle, status } = getValue();

  if (!programTitle || !treatmentTitle || !programId) {
    return <Fallback />;
  }

  const isStarted = !!status?.startedAt;
  const startedAt = status?.startedAt
    ? format(status.startedAt, "yyyy-MM-dd")
    : "--";
  const completedAt = status?.completedAt
    ? format(status.completedAt, "yyyy-MM-dd")
    : "--";

  const children = status ? statusCell(status) : null;

  const getTooltipTitle = () => {
    if (isLocked) {
      return t("ICBT_LOCKED_TOOLTIP");
    }
    if (nextScheduledExecution) {
      return t("ICBT_NEXT_SCHEDULED_EXECUTION", {
        date: formatRelativeDate(nextScheduledExecution),
      });
    }
    if (isStarted) {
      return t("ICBT_HOVER_DETAILS", {
        startedAt,
        completedAt,
      });
    }
    return null;
  };

  const getStatusIcon = () => {
    if (isLocked) return <LockIcon />;
    if (status?.completedAt) {
      return <CheckCircleIcon $highlight={rowStatus ?? "none"} />;
    }
    return <AssignmentIcon $highlight={rowStatus ?? "none"} />;
  };

  return (
    <IconTextContainer>
      <Tooltip content={getTooltipTitle()} placement="bottom-start">
        {getStatusIcon()}
      </Tooltip>
      <ProgramTreatmentCellText
        sortKey={programTitle}
        programName={programTitle}
        treatmentName={treatmentTitle}
        handleClickProgramTitle={() => openPreviewModal({ programId })}
      >
        {children}
      </ProgramTreatmentCellText>
    </IconTextContainer>
  );
};

export const feedbackDateCell = (
  getValue: Getter<AssignedTableRow["feedbackDate"]>,
) => {
  const { feedbackDate, feedbackDateIgnored, hasFeedback } = getValue();

  if (!feedbackDate) {
    return <Fallback />;
  }

  return (
    <FeedbackDateCellText
      key={feedbackDate}
      feedbackDate={feedbackDate}
      feedbackDateIgnored={feedbackDateIgnored}
      hasFeedback={hasFeedback}
    />
  );
};

export const patientNotesCell = (
  getValue: Getter<AssignedTableRow["patientNotes"]>,
) => {
  const data = getValue();

  if (!data) return <Fallback />;

  const {
    patientNotesCount,
    questionnairesInProgramAnswered,
    totalQuestionnairesInProgram,
  } = data;

  return (
    <PatientNotesCellText
      patientNoteCount={patientNotesCount}
      questionnairesInProgramAnswered={questionnairesInProgramAnswered}
      totalQuestionnairesInProgram={totalQuestionnairesInProgram}
    />
  );
};

export const statusCell = (value: ProgramStatus | null) => {
  if (!value) return null;

  const { completedAt, percentage } = value;

  if (completedAt) return null;

  return <StatusInProgressCell sortKey={percentage} percentage={percentage} />;
};

export const icbtFeedbackCell = ({
  getValue,
  isFeedbackLocked,
  feedbackLockReason,
  t,
}: {
  getValue: Getter<AssignedTableRow["icbtFeedbackState"]>;
  isFeedbackLocked?: boolean;
  feedbackLockReason?: FeedbackLockReason;
  t: TFunction<"translation", undefined>;
}) => {
  const { feedbacks, communication } = getValue();

  if (!communication) return <Fallback />;

  return (
    <IconTextContainer>
      {isFeedbackLocked && (
        <Tooltip
          content={t(getFeedbackLockTranslationKey(feedbackLockReason))}
          placement="bottom-start"
        >
          <LockIcon />
        </Tooltip>
      )}
      <IcbtFeedbackStateCell
        displayDropdownUpwards
        feedbacks={feedbacks}
        communication={communication}
        isFeedbackLocked={isFeedbackLocked}
      />
    </IconTextContainer>
  );
};

export const assignedCell = (
  getValue: Getter<AssignedTableRow["assigned"]>,
) => {
  const { assignedAt, assignedBy } = getValue();

  if (!assignedAt) return <Fallback />;

  return (
    <AssignedCellWithIcon
      sortKey={assignedAt.getTime()}
      assignedAt={assignedAt}
      assignedBy={assignedBy}
    />
  );
};

const Fallback = () => <Typography variant="body-small">{"--"}</Typography>;
